import { fail, success } from '@helpers/result-helper.js'
import { isSuccess, isHttpStatus } from '@/helpers/http-status-helpers'
import toast from '@services/toasts/index.js'

// eslint-disable-next-line no-unused-vars
import SubBubbleAgreementViewModel from '@/models/subBubbleAgreement/subBubbleAgreementViewModel'
// eslint-disable-next-line no-unused-vars
import CreateSubBubbleAgreementDTO from '@/models/subBubbleAgreement/createSubBubbleAgreementDTO'
// eslint-disable-next-line no-unused-vars
import UpdateSubBubbleAgreementDTO from '@/models/subBubbleAgreement/updateSubBubbleAgreementDTO'

const getDefaultState = () => {
  return {
    loadingCount: 0,
    crudLoadingCount: 0,
  }
}

const state = getDefaultState()

export default {
  namespaced: true,
  /**
   * @typedef {{
   * loadingCount: number
   * crudLoadingCount: number
   * }} SubBubbleAgreementState
   * @type {SubBubbleAgreementState}
   */
  state,
  getters: {
    /**
     * @returns {Boolean}
     */
    isLoading: (state) => state.loadingCount > 0,
    /**
     * @returns {Boolean}
     */
    isLoadingCRUD: (state) => state.crudLoadingCount > 0,
  },
  mutations: {
    START_LOADING(state) {
      state.loadingCount++
    },
    FINISH_LOADING(state) {
      state.loadingCount--
    },
    START_LOADING_CRUD(state) {
      state.crudLoadingCount++
    },
    FINISH_LOADING_CRUD(state) {
      state.crudLoadingCount--
    },
    CLEAR_STORE(state) {
      // Resets store to default state
      Object.assign(state, getDefaultState())
    },
  },
  actions: {
    /**
     * @param {{commit: Function, dispatch: Function, getters: Object}} vuexContext
     * @param {Number} clientId
     * @returns {SubBubbleAgreementViewModel[]}
     */
    async getByClientId({ commit }, clientId) {
      commit('START_LOADING')

      try {
        const response = await this.$api.client.getSubBubbleAgreements(clientId)
        if (isSuccess(response.status)) {
          return success(response.data)
        }
      } catch (ex) {
        toast.error(
          this.$i18n.t('subBubbleAgreements.errorMessages.failedToLoad')
        )
        return fail()
      } finally {
        commit('FINISH_LOADING')
      }
    },
    /**
     * @param {{commit: Function, dispatch: Function, getters: Object}} vuexContext
     * @param {Number} clientGroupId
     * @returns {SubBubbleAgreementViewModel[]}
     */
    async getByClientGroupId({ commit }, clientGroupId) {
      commit('START_LOADING')

      try {
        const response = await this.$api.clientGroup.getSubBubbleAgreements(
          clientGroupId
        )
        if (isSuccess(response.status)) {
          return success(response.data)
        }
      } catch (ex) {
        toast.error(
          this.$i18n.t('subBubbleAgreements.errorMessages.failedToLoad')
        )
        return fail()
      } finally {
        commit('FINISH_LOADING')
      }
    },
    /**
     * @param {{commit: Function, dispatch: Function, getters: Object}} vuexContext
     * @param {CreateSubBubbleAgreementDTO} payload
     * @returns {SubBubbleAgreementViewModel}
     */
    async create({ commit }, payload) {
      commit('START_LOADING_CRUD')

      try {
        const response = await this.$api.subBubbleAgreement.post('', payload)
        if (isSuccess(response.status)) {
          toast.success(
            this.$i18n.t('subBubbleAgreements.successMessages.createSuccess')
          )
          return success(response.data)
        }
      } catch (ex) {
        if (isHttpStatus(ex.response.status, 'Conflict'))
          toast.error(
            this.$i18n.t('subBubbleAgreements.errorMessages.conflict')
          )
        else
          toast.error(
            this.$i18n.t('subBubbleAgreements.errorMessages.failedToCreate')
          )

        return fail()
      } finally {
        commit('FINISH_LOADING_CRUD')
      }
    },

    /**
     * @param {{commit: Function, dispatch: Function, getters: Object}} vuexContext
     * @param {UpdateSubBubbleAgreementDTO} payload
     * @returns {SubBubbleAgreementViewModel}
     */
    async update({ commit }, payload) {
      commit('START_LOADING_CRUD')

      try {
        const response = await this.$api.subBubbleAgreement.put(
          payload.id,
          payload
        )
        if (isSuccess(response.status)) {
          toast.success(
            this.$i18n.t('subBubbleAgreements.successMessages.updateSuccess')
          )
          return success(response.data)
        }
      } catch (ex) {
        if (isHttpStatus(ex.response.status, 'Conflict'))
          toast.error(
            this.$i18n.t('subBubbleAgreements.errorMessages.conflict')
          )
        else
          toast.error(
            this.$i18n.t('subBubbleAgreements.errorMessages.failedToUpdate')
          )

        return fail()
      } finally {
        commit('FINISH_LOADING_CRUD')
      }
    },

    /**
     * @param {{commit: Function, dispatch: Function, getters: Object}} vuexContext
     * @param {Number} id
     */
    async delete({ commit }, id) {
      commit('START_LOADING_CRUD')

      try {
        const response = await this.$api.subBubbleAgreement.delete(id)
        if (isSuccess(response.status)) {
          toast.success(
            this.$i18n.t('subBubbleAgreements.successMessages.deleteSuccess')
          )
          return success(response.data)
        }
      } catch (ex) {
        toast.error(
          this.$i18n.t('subBubbleAgreements.errorMessages.failedToDelete')
        )
        return fail()
      } finally {
        commit('FINISH_LOADING_CRUD')
      }
    },

    /**
     * Resets store to default state.
     */
    clear({ commit }) {
      commit('CLEAR_STORE')
    },
  },
}
